import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Drawer,
  useMediaQuery,
  useTheme,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import { Person, Schedule, Close, ExpandMore } from "@mui/icons-material";
import "./CertificateEachSelectionComponent.css";
import WorkshopFooterPage from "../Workshops/WorkshopFooter";
import RecognizedBySection from "./RecognizedBySection";
import { useHistory } from "react-router-dom/cjs/react-router-dom";
import { useParams } from "react-router-dom/cjs/react-router-dom.min";
import { useDispatch, useSelector } from "react-redux";
import { getCertificateBySlugAction } from "../../../actions/MyCertificates/certificatesAction";
import MySkeletonLoader from "../reusables/MySkeletonLoader";
import EducationTopBar from "../topbar/EducationTopBar";
import useWindowResize from "../../../utils/WindowResize";

const CertificateEachSelectionComponent = () => {
  const history = useHistory();
  const urlData = useParams();
  const dispatch = useDispatch();
  const isSmallScreen = useWindowResize();
  useEffect(() => {
    if (urlData?.name) {
      dispatch(getCertificateBySlugAction(urlData?.name));
    }
  }, []);
  const { certifications, loading } = useSelector(
    (state) => state.certificateGetBySlugStore,
  );
  const [selectedCertificate, setSelectedCertificate] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const faqData = [
    {
      question: "How long does it take to complete a certificate?",
      answer:
        "Most certificates can be completed in 10 days if you dedicate 2-3 hours daily. However, you can learn at your own pace as there are no strict deadlines. The certificate remains accessible to you even after completion.",
    },
    {
      question: "Will this certificate help me get a job?",
      answer:
        "Yes! Our certificates are project-based and focus on practical skills that employers look for. You'll build real-world projects that you can showcase in your portfolio and discuss during technical interviews. Many of our students have successfully landed jobs and internships after completing these certificates.",
    },
    {
      question: "Do I get lifetime access to the content?",
      answer:
        "Absolutely! Once you enroll in a certificate program, you get lifetime access to all the content, including any future updates. You can revisit the materials anytime you need a refresher or want to explore advanced topics.",
    },
  ];

  const handleCardClick = (certificate) => {
    setSelectedCertificate(certificate);
  };

  const handleClose = () => {
    setSelectedCertificate(null);
  };

  function getTagLabel(tag) {
    const tagMap = {
      most_bought: "Most Bought",
      most_liked: "Most Liked",
      students_choice: "Students' Choice",
      trending: "Trending",
      new: "New",
    };

    return tagMap[tag] || ""; // returns empty string if tag doesn't match
  }

  return (
    <div>
      <EducationTopBar
        logo={
          <div
            onClick={() => history.push("/")}
            style={{ display: "flex", alignItems: "center", cursor: "pointer" }}
          >
            <img
              className={isSmallScreen ? "mobile-logo" : "desktop-logo"}
              src="https://res.cloudinary.com/cqn/image/upload/v1740519381/white_acetechies_logo_vsdhp5.png"
              alt=""
            />
          </div>
        }
      />
      <div className="certificateEachSelectionComponent__container">
        <h1 className="certificateEachSelectionComponent__title">
          Select your favourite topic in Frontend Development
        </h1>

        {loading ? (
          <div className="certificateDashboard__loader">
            <MySkeletonLoader height={"500px"} />
            <MySkeletonLoader height={"500px"} />
            <MySkeletonLoader height={"500px"} />
          </div>
        ) : (
          <div className="certificateEachSelectionComponent__grid">
            {certifications
              ?.slice()
              .sort((a, b) => a?.order - b?.order)
              ?.map((cert) => (
                <motion.div
                  key={cert?._id}
                  className="certificateEachSelectionComponent__card"
                  onClick={() => handleCardClick(cert)}
                  whileHover={{ y: -8, transition: { duration: 0.3 } }}
                  whileTap={{ scale: 0.98 }}
                >
                  {cert?.tag && (
                    <div className="certificateEachSelectionComponent__badge">
                      {getTagLabel(cert?.tag)}
                    </div>
                  )}

                  <div className="certificateEachSelectionComponent__cardImage">
                    <img src={cert?.image?.url} alt={cert?.image?.alt} />
                  </div>

                  <div className="certificateEachSelectionComponent__cardContent">
                    <div className="certificateEachSelectionComponent__enrollmentInfo">
                      <Person
                        style={{ color: "#8A2BE2", marginBottom: "2px" }}
                        className="certificateEachSelectionComponent__enrollmentIcon"
                      />
                      <span className="certificateEachSelectionComponent__enrollmentCount">
                        {cert?.registeredUsers}
                      </span>
                      <span className="certificateEachSelectionComponent__enrollmentLabel">
                        registered
                      </span>
                    </div>

                    <h3 className="certificateEachSelectionComponent__cardTitle">
                      {cert?.title}
                    </h3>
                    <p className="certificateEachSelectionComponent__cardSubtitle">
                      {cert?.subtitle}
                    </p>

                    <button className="certificateEachSelectionComponent__viewButton">
                      View Details
                    </button>
                  </div>
                </motion.div>
              ))}
          </div>
        )}

        <div style={{ margin: "0px 20px" }}>
          <RecognizedBySection />
        </div>

        <div className="certificateEachSelectionComponent__faqSection">
          <h2 className="certificateEachSelectionComponent__faqTitle">
            Frequently Asked Questions
          </h2>

          {faqData.map((faq, index) => (
            <Accordion
              key={index}
              sx={{
                backgroundColor: "#1a1a1a",
                color: "#ffffff",
                border: "1px solid #2a2a2a",
                marginBottom: "12px",
                "&:before": {
                  display: "none",
                },
                "&.Mui-expanded": {
                  margin: "0 0 12px 0",
                },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMore sx={{ color: "#ffffff" }} />}
                sx={{
                  "&:hover": {
                    backgroundColor: "#222222",
                  },
                }}
              >
                <span className="certificateEachSelectionComponent__faqQuestion">
                  {faq.question}
                </span>
              </AccordionSummary>
              <AccordionDetails>
                <p className="certificateEachSelectionComponent__faqAnswer">
                  {faq.answer}
                </p>
              </AccordionDetails>
            </Accordion>
          ))}
        </div>

        <Drawer
          anchor={isMobile ? "bottom" : "right"}
          open={Boolean(selectedCertificate)}
          onClose={handleClose}
          PaperProps={{
            sx: {
              width: isMobile ? "100%" : "500px",
              maxWidth: "100%",
              height: isMobile ? "90vh" : "100%",
              borderTopLeftRadius: isMobile ? "20px" : 0,
              borderTopRightRadius: isMobile ? "20px" : 0,
              backgroundColor: "#1a1a1a",
              // position: "relative",
            },
          }}
        >
          <AnimatePresence>
            {selectedCertificate && (
              <motion.div
                className="certificateEachSelectionComponent__drawer"
                initial={{
                  opacity: 0,
                  y: isMobile ? 50 : 0,
                  x: isMobile ? 0 : 50,
                }}
                animate={{ opacity: 1, y: 0, x: 0 }}
                exit={{
                  opacity: 0,
                  y: isMobile ? 50 : 0,
                  x: isMobile ? 0 : 50,
                }}
                transition={{ duration: 0.3 }}
              >
                <IconButton
                  className="certificateEachSelectionComponent__closeButton"
                  onClick={handleClose}
                  sx={{ color: "white" }}
                >
                  <Close />
                </IconButton>
                <div
                  className="certificateEachSelectionComponent__drawerMainContent"
                  style={{ overflow: "auto" }}
                >
                  <div className="certificateEachSelectionComponent__drawerContent">
                    <div className="certificateEachSelectionComponent__drawerImage">
                      <img
                        src={selectedCertificate?.image?.url}
                        alt={selectedCertificate?.image?.alt}
                      />
                    </div>

                    <div className="certificateEachSelectionComponent__drawerStats">
                      <div className="certificateEachSelectionComponent__stat">
                        <Person style={{ color: "#8A2BE2" }} />
                        <span>
                          {selectedCertificate?.registeredUsers} Registered
                        </span>
                      </div>
                      <div className="certificateEachSelectionComponent__stat">
                        <Schedule style={{ color: "#8A2BE2" }} />
                        <span>
                          {selectedCertificate?.daysToComplete?.displayText} to
                          complete
                        </span>
                      </div>
                    </div>

                    <h2 className="certificateEachSelectionComponent__drawerTitle">
                      {selectedCertificate?.title}
                    </h2>
                    <p className="certificateEachSelectionComponent__drawerSubtitle">
                      {selectedCertificate?.subtitle}
                    </p>

                    <div className="certificateEachSelectionComponent__techChips">
                      {selectedCertificate?.techStack?.map((tech, index) => (
                        <div
                          key={index}
                          className="certificateEachSelectionComponent__chip"
                        >
                          <img
                            src={tech?.icon}
                            alt={tech?.name}
                            className="certificateEachSelectionComponent__chipIcon"
                          />
                          <span>{tech?.name}</span>
                        </div>
                      ))}
                    </div>

                    <div className="certificateEachSelectionComponent__benefitsSection">
                      <h3 className="certificateEachSelectionComponent__benefitsTitle">
                        How this helps you in getting your next job/internship
                      </h3>
                      <ul className="certificateEachSelectionComponent__benefitsList">
                        {selectedCertificate?.jobBenefits?.map(
                          (benefit, index) => (
                            <li key={index}>{benefit}</li>
                          ),
                        )}
                      </ul>
                    </div>
                  </div>
                </div>
                <button
                  onClick={() =>
                    history.push(
                      `/certificates/details/${selectedCertificate?._id}`,
                    )
                  }
                  className="certificateEachSelectionComponent__certificateButton"
                >
                  <span>Get my certificate</span>
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </Drawer>
        <WorkshopFooterPage />
      </div>
    </div>
  );
};

export default CertificateEachSelectionComponent;
